import { useQueryClient } from '@tanstack/react-query';
import { MigrationCompatibilityReport } from '@erp/shared-types';
import useGetApi from 'src/api/useGetApi';
import useMutationApi from 'src/api/useMutationApi';
import type {
  VersionInfo,
  RebateAgreementWithStructure,
  CreateVersionRequest,
  MigrateCSVsRequest,
} from './types';

// ==================== List All Versions ====================

export function useListVersions(versionGroupId: string, enabled = true) {
  return useGetApi<{ versions: VersionInfo[] }, VersionInfo[]>({
    endpoint: `rebates/agreements/${versionGroupId}/versions`,
    options: {
      enabled: enabled && !!versionGroupId,
    },
    transformResponse: (data) => data.versions,
  });
}

// ==================== Get Specific Version ====================

export function useVersion(
  versionGroupId: string,
  versionNumber: number,
  enabled = true,
) {
  return useGetApi<
    { version: RebateAgreementWithStructure },
    RebateAgreementWithStructure
  >({
    endpoint: `rebates/agreements/${versionGroupId}/versions/${versionNumber}`,
    options: {
      enabled: enabled && !!versionGroupId && !!versionNumber,
    },
    transformResponse: (data) => data.version,
  });
}

// ==================== Create New Version ====================

export function useCreateVersion(versionGroupId: string) {
  const queryClient = useQueryClient();

  return useMutationApi<
    CreateVersionRequest,
    {
      version: RebateAgreementWithStructure;
      message: string;
    }
  >({
    endpoint: `rebates/agreements/${versionGroupId}/versions`,
    method: 'POST',
    successMessage: 'Version created successfully',
    transformRequest: (data) => {
      // If files are provided, use FormData
      if (data.files && data.files.length > 0) {
        const formData = new FormData();
        if (data.copyFromVersionNumber) {
          formData.append(
            'copyFromVersionNumber',
            String(data.copyFromVersionNumber),
          );
        }
        data.files.forEach((file) => {
          formData.append('files', file);
        });
        return formData;
      }
      // Otherwise, send JSON
      return data;
    },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [`rebates/agreements/${versionGroupId}/versions`],
        });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', versionGroupId],
        });
      },
    },
  });
}

// ==================== Finalize Version ====================

export function useFinalizeVersion(
  versionGroupId: string,
  versionNumber: number,
) {
  const queryClient = useQueryClient();

  return useMutationApi<Record<string, never>, { message: string }>({
    endpoint: `rebates/agreements/${versionGroupId}/versions/${versionNumber}/finalize`,
    method: 'PUT',
    successMessage: 'Version finalized successfully',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [`rebates/agreements/${versionGroupId}/versions`],
        });
        queryClient.invalidateQueries({
          queryKey: [
            `rebates/agreements/${versionGroupId}/versions/${versionNumber}`,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', versionGroupId],
        });
      },
    },
  });
}

// ==================== Delete Draft Version ====================

export function useDeleteVersion(
  versionGroupId: string,
  versionNumber: number,
) {
  const queryClient = useQueryClient();

  return useMutationApi<Record<string, never>, { message: string }>({
    endpoint: `rebates/agreements/${versionGroupId}/versions/${versionNumber}`,
    method: 'DELETE',
    successMessage: 'Version deleted successfully',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [`rebates/agreements/${versionGroupId}/versions`],
        });
        queryClient.invalidateQueries({
          queryKey: ['rebateAgreement', versionGroupId],
        });
      },
    },
  });
}

// ==================== CSV Migration Analysis ====================

export function useMigrationAnalysis(
  versionGroupId: string,
  targetVersionNumber: number,
  sourceVersionNumber: number | null,
  enabled = true,
) {
  return useGetApi<MigrationCompatibilityReport>({
    endpoint: `rebates/agreements/${versionGroupId}/versions/${targetVersionNumber}/migration-analysis`,
    queryParameters: {
      sourceVersionNumber,
    },
    options: {
      enabled: enabled && !!versionGroupId && !!sourceVersionNumber,
    },
  });
}

// ==================== Migrate CSVs ====================

export function useMigrateCSVs(
  versionGroupId: string,
  targetVersionNumber: number,
) {
  const queryClient = useQueryClient();

  return useMutationApi<
    MigrateCSVsRequest,
    {
      message: string;
      migratedCount: number;
    }
  >({
    endpoint: `rebates/agreements/${versionGroupId}/versions/${targetVersionNumber}/migrate-csvs`,
    method: 'POST',
    successMessage: 'CSVs migrated successfully',
    options: {
      onSuccess: () => {
        // Invalidate invoice queries for all versions
        queryClient.invalidateQueries({
          queryKey: ['rebateInvoices'],
        });
        queryClient.invalidateQueries({
          queryKey: [
            `rebates/agreements/${versionGroupId}/versions/${targetVersionNumber}`,
          ],
        });
      },
    },
  });
}
